import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Box, IconButton } from '@mui/material'; 
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'; 
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'; 

import GeometrySubMenu from "./geomSubMenu";
import GeoDropDown from "./geomDropDown";
import FormComponent from "../form/requestForm";
import useAuth from '../../hooks/useAuth';
import api_service from "../../api/api_service";
import MasterRequest from "../request/masterRequestComponent";

import LayerManager from "./LayerManager";

const MapComponent = ({ isFormSubmenuOpen, setIsFormSubmenuOpen, isRequestSubmenuOpen, setIsRequestSubmenuOpen }) => {
    useAuth()

    const [isDropdownOpen, setIsDropdownOpen] = useState(true);

    const [geojsonData, setGeojsonData] = useState([]);
    const [selectedGeom, setSelectedGeom] = useState(null);
    const [editableGeom, setEditableGeom] = useState(null); // This state is still here, but won't be modified by the map
    const [isGeomSubmenuOpen, setIsGeomSubmenuOpen] = useState(false);
    const [requestGeomLayer, setRequestGeomLayer] = useState(null);
    const [replicatedRequest, setReplicatedRequest] = useState(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const mapRef = useRef(null);
    const drawnItemsRef = useRef(null);
    const drawControlRef = useRef(null);
    const measurementLayerRef = useRef(null);
    const measurementPointsRef = useRef([]);
    const [isMeasuring, setIsMeasuring] = useState(false);

    const mapContainerStyle = {
        height: "100%",
        width: "100%",
        flexGrow: 1,
        minHeight: 0,
        transition: "width 0.3s ease-in-out",
    };

    const escapeHtml = (value = "") => String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const resolveHexColor = (color = "") => {
        if (typeof color !== "string") return null;
        const trimmed = color.trim();
        if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) {
            return null;
        }
        if (trimmed.length === 7) {
            return trimmed;
        }
        return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`;
    };

    const getContrastText = (color = "") => {
        const hex = resolveHexColor(color) || "#546E7A";
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.6 ? "#1a1a1a" : "#fafafa";
    };

    const renderClassBadges = (entries = []) => {
        if (!entries.length) {
            return `<span style="color:#757575;">None</span>`;
        }
        const badges = entries.map((entry) => {
            const safeLabel = escapeHtml(entry?.label ?? "Unknown");
            const bg = resolveHexColor(entry?.color) || "#546E7A";
            const textColor = getContrastText(bg);
            return `<span style="display:inline-flex;align-items:center;padding:2px 6px;margin:2px 6px 2px 0;border-radius:999px;font-size:11px;font-weight:600;background:${bg};color:${textColor};">${safeLabel}</span>`;
        }).join("");
        return `<div style="display:flex;flex-wrap:wrap;margin-top:4px;">${badges}</div>`;
    };

    const isClassInfoArray = (value) => Array.isArray(value)
        && value.every((entry) => entry && typeof entry === "object" && "label" in entry);

    const isClassInfoEntry = (key, value) => typeof key === "string"
        && key.toLowerCase().endsWith("_classes")
        && isClassInfoArray(value);

    const formatKey = (key = "") => key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

    const formatValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "N/A";
        }
        if (typeof value === "number") {
            if (Number.isNaN(value)) return "N/A";
            return Number.isInteger(value) ? value : Number(value).toFixed(3);
        }
        if (Array.isArray(value)) {
            return value.map((entry) => formatValue(entry)).join(", ");
        }
        if (typeof value === "object") {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }
        return value;
    };

    const buildPopupContent = (properties = {}) => {
        const header = properties.__modelType
            ? `<div style="font-weight:600;margin-bottom:4px;">Model: ${properties.__modelType}</div>`
            : "";
        const entries = Object.entries(properties).filter(([key]) => !key.startsWith("__"));
        const rendered = entries
            .map(([key, value]) => {
                if (isClassInfoEntry(key, value)) {
                    return `<div><strong>${formatKey(key)}:</strong> ${renderClassBadges(value)}</div>`;
                }
                if (Array.isArray(value) && value.length === 0) {
                    return null;
                }
                return `<div><strong>${formatKey(key)}:</strong> ${formatValue(value)}</div>`;
            })
            .filter(Boolean);
        if (!rendered.length) {
            return `<div>${header}<div>No additional data for this cell.</div></div>`;
        }
        const body = rendered.join("");
        return `<div>${header}${body}</div>`;
    };

    const styleResultFeature = (feature = {}) => {
        const layerType = feature.properties?.__layerType || feature.properties?.type;
        if (layerType === "original") {
            return { color: "orange", weight: 2, opacity: 0.7, fillOpacity: 0.05, dashArray: "4 4" };
        }
        return { color: "#6a1b9a", weight: 2, opacity: 0.9, fillColor: "#9575cd", fillOpacity: 0.35 };
    };

    const normalizeResultFeatures = (result) => {
        if (!result) return [];

        const normalizedFeatures = [];
        const modelLabel = result.model_type ? result.model_type.toUpperCase() : "";

        if (Array.isArray(result.results_geojson?.features) && result.results_geojson.features.length > 0) {
            result.results_geojson.features.forEach((feature, index) => {
                if (!feature?.geometry) return;
                normalizedFeatures.push({
                    type: "Feature",
                    geometry: feature.geometry,
                    properties: {
                        ...(feature.properties || {}),
                        model_type: result.model_type || "",
                        __layerType: feature.properties?.__layerType || "result",
                        __modelType: modelLabel,
                        __featureIdx: index
                    }
                });
            });
        } else if (result.results && typeof result.results === "object" && Object.keys(result.results).length > 0) {
            normalizedFeatures.push({
                type: "Feature",
                geometry: result.results,
                properties: {
                    model_type: result.model_type || "",
                    __layerType: "result",
                    __modelType: modelLabel
                }
            });
        }

        return normalizedFeatures;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await api_service.getGeoms();
                setGeojsonData(data);
            } catch (error) {
                setGeojsonData([]);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (!isMapReady || !mapRef.current) {
            return;
        }

        const map = mapRef.current;

        if (!drawnItemsRef.current) {
            drawnItemsRef.current = new L.FeatureGroup();
            map.addLayer(drawnItemsRef.current);
        }

        if (!drawControlRef.current) {
            drawControlRef.current = new L.Control.Draw({
                edit: {
                    featureGroup: drawnItemsRef.current
                },
                draw: {
                    polygon: true,
                    polyline: false,
                    rectangle: false,
                    circle: false,
                    marker: false,
                    circlemarker: false
                }
            });
            map.addControl(drawControlRef.current);
        }

        const handleDrawCreated = (event) => {
            const layer = event.layer;
            if (!layer || !(layer instanceof L.Polygon)) {
                return;
            }

            drawnItemsRef.current.addLayer(layer);
            const geojson = layer.toGeoJSON();
            const sanitizedGeometry = geojson?.geometry;

            if (sanitizedGeometry) {
                const newGeometry = { geometry: sanitizedGeometry };
                setSelectedGeom(newGeometry);
                setEditableGeom(newGeometry);
                setIsGeomSubmenuOpen(true);
                setIsFormSubmenuOpen(false);
                setIsRequestSubmenuOpen(false);
            }

            const bounds = layer.getBounds();
            if (bounds && bounds.isValid()) {
                map.fitBounds(bounds);
            }
        };

        map.on(L.Draw.Event.CREATED, handleDrawCreated);

        return () => {
            map.off(L.Draw.Event.CREATED, handleDrawCreated);
            if (drawControlRef.current) {
                map.removeControl(drawControlRef.current);
                drawControlRef.current = null;
            }
            if (drawnItemsRef.current) {
                map.removeLayer(drawnItemsRef.current);
                drawnItemsRef.current = null;
            }
        };
    }, [isMapReady, setEditableGeom, setIsFormSubmenuOpen, setIsGeomSubmenuOpen, setIsRequestSubmenuOpen, setSelectedGeom]);

    const handleViewRequestGeometry = (payload) => {
        if (!mapRef.current) return;
        const map = mapRef.current;

        if (requestGeomLayer) {
            map.removeLayer(requestGeomLayer);
            setRequestGeomLayer(null);
        }

        let featureCollection = null;

        if (payload) {
            if (payload.type === "FeatureCollection") {
                const normalizedFeatures = (payload.features || []).map((feature) => ({
                    ...feature,
                    properties: {
                        ...(feature.properties || {}),
                        __layerType: feature.properties?.__layerType || (feature.properties?.type === "original" ? "original" : "result")
                    }
                }));
                featureCollection = { ...payload, features: normalizedFeatures };
            } else if (payload.type === "Feature" && payload.geometry) {
                featureCollection = {
                    type: "FeatureCollection",
                    features: [{
                        ...payload,
                        properties: {
                            ...(payload.properties || {}),
                            __layerType: payload.properties?.__layerType || "result"
                        }
                    }]
                };
            } else {
                const { result, originalGeometry } = payload;
                const features = [];
                if (originalGeometry && originalGeometry.type && originalGeometry.coordinates) {
                    features.push({
                        type: "Feature",
                        geometry: originalGeometry,
                        properties: { __layerType: "original" }
                    });
                }

                features.push(...normalizeResultFeatures(result));

                if (features.length > 0) {
                    featureCollection = { type: "FeatureCollection", features };
                }
            }
        }

        if (featureCollection) {
            try {
                const geoJsonLayer = L.geoJSON(featureCollection, {
                    style: styleResultFeature,
                    onEachFeature: (feature, layer) => {
                        const popupContent = buildPopupContent(feature.properties || {});
                        layer.bindPopup(popupContent);
                    }
                }).addTo(map);
                setSelectedGeom(null);
                setRequestGeomLayer(geoJsonLayer);
                const bounds = geoJsonLayer.getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds);
                }
            } catch (error) {
                console.error("Error adding request geometry to map:", error);
            }
        }
    };

    useEffect(() => {
        if (!isRequestSubmenuOpen && requestGeomLayer && mapRef.current) {
            mapRef.current.removeLayer(requestGeomLayer);
            setRequestGeomLayer(null);
        }
    }, [isRequestSubmenuOpen, requestGeomLayer]);

    const handlePrefillConsumed = useCallback(() => {
        setReplicatedRequest(null);
    }, []);

    const handleReplicateRequest = useCallback((payload) => {
        if (!payload || !payload.args) {
            console.warn("Invalid payload for replication", payload);
            return;
        }
        const geometryDetails = payload.geometry;
        if (geometryDetails?.geometry) {
            const geometryFeature = {
                ...geometryDetails,
                geometry: geometryDetails.geometry
            };
            setSelectedGeom(geometryFeature);
            setEditableGeom(geometryFeature);
        } else {
            setSelectedGeom(null);
            setEditableGeom(null);
        }
        setReplicatedRequest(payload);
        setIsFormSubmenuOpen(true);
        setIsRequestSubmenuOpen(false);
        setIsGeomSubmenuOpen(false);
    }, [setIsFormSubmenuOpen, setIsRequestSubmenuOpen, setIsGeomSubmenuOpen]);

    const handleGeomUpdate = (updatedGeom) => {
        updatedGeom.then((updatedGeom) => {
            setGeojsonData((prevGeoms) => {
                const currentGeoms = Array.isArray(prevGeoms) ? prevGeoms : [];
                if (Array.isArray(updatedGeom)) {
                    return [...currentGeoms, ...updatedGeom];
                } else {
                    const existingIndex = currentGeoms.findIndex((geom) => geom.geom_id === updatedGeom.geom_id);
                    if (existingIndex !== -1) {
                        const updatedGeoms = [...currentGeoms];
                        updatedGeoms[existingIndex] = updatedGeom;
                        return updatedGeoms;
                    } else {
                        return [...currentGeoms, updatedGeom];
                    }
                }
            });
        })
    };

    const handleClick = (feature) => {
        if (mapRef.current && requestGeomLayer) {
            const map = mapRef.current;
            if (map.hasLayer(requestGeomLayer)) {
                map.removeLayer(requestGeomLayer);
            }
            setRequestGeomLayer(null);
        }
        setSelectedGeom(feature);
        setEditableGeom(feature); // We still set this for the submenu, even if it can't be edited on the map
        if (!isFormSubmenuOpen && !isRequestSubmenuOpen) {
            setIsGeomSubmenuOpen(true);
        }
        setTimeout(() => {
            if (mapRef.current && feature.geometry) {
                const geoJsonLayer = L.geoJSON(feature.geometry);
                const bounds = geoJsonLayer.getBounds();
                if (bounds.isValid()) { // Check if bounds are valid before fitting
                    mapRef.current.fitBounds(bounds);
                }
            }
        }, 100);
    };

    const handleCloseMenu = () => {
        setSelectedGeom(null);
        setIsGeomSubmenuOpen(false);
    };

    const ensureMeasurementLayer = () => {
        if (!mapRef.current) return null;
        if (!measurementLayerRef.current) {
            measurementLayerRef.current = L.layerGroup().addTo(mapRef.current);
        }
        return measurementLayerRef.current;
    };

    const clearMeasurement = useCallback(() => {
        measurementPointsRef.current = [];
        if (measurementLayerRef.current) {
            measurementLayerRef.current.clearLayers();
        }
        if (mapRef.current) {
            mapRef.current.closePopup();
        }
    }, []);

    const formatDistance = (meters = 0) => {
        if (!meters || Number.isNaN(meters)) {
            return "0 m";
        }
        if (meters >= 1000) {
            return `${(meters / 1000).toFixed(2)} km`;
        }
        if (meters >= 100) {
            return `${meters.toFixed(1)} m`;
        }
        return `${meters.toFixed(0)} m`;
    };

    const handleMeasurementClick = useCallback((latlng) => {
        if (!isMeasuring || !mapRef.current || !latlng) {
            return false;
        }
        const layerGroup = ensureMeasurementLayer();
        if (!layerGroup) {
            return false;
        }
        const marker = L.circleMarker(latlng, {
            radius: 5,
            color: "#ff5722",
            weight: 2,
            fillColor: "#fff",
            fillOpacity: 1
        });
        marker.addTo(layerGroup);
        measurementPointsRef.current.push(latlng);
        if (measurementPointsRef.current.length === 2) {
            const [start, end] = measurementPointsRef.current;
            const polyline = L.polyline([start, end], {
                color: "#ff5722",
                weight: 2,
                dashArray: "6 4"
            }).addTo(layerGroup);
            const distance = mapRef.current.distance(start, end);
            const midpoint = L.latLng(
                (start.lat + end.lat) / 2,
                (start.lng + end.lng) / 2
            );
            const popup = L.popup({
                closeButton: false,
                autoClose: false,
                offset: L.point(0, -8)
            })
                .setLatLng(midpoint)
                .setContent(`<strong>Distance:</strong> ${formatDistance(distance)}`)
                .openOn(mapRef.current);
            layerGroup.addLayer(polyline);
            layerGroup.addLayer(popup);
            measurementPointsRef.current = [];
        }
        return true;
    }, [isMeasuring]);

    const handleToggleMeasurement = () => {
        setIsMeasuring(prev => {
            const next = !prev;
            if (!next) {
                clearMeasurement();
            } else {
                ensureMeasurementLayer();
            }
            return next;
        });
    };

    useEffect(() => {
        if (!isMeasuring) {
            clearMeasurement();
        }
    }, [isMeasuring, clearMeasurement]);

    const MapClickHandler = ({ onMapClick }) => {
        useMapEvents({
            click: (event) => {
                if (typeof onMapClick === "function") {
                    onMapClick(event);
                }
            },
        });
        return null;
    };

    const handleMapClick = useCallback((event) => {
        if (isMeasuring && event?.latlng) {
            handleMeasurementClick(event.latlng);
            return;
        }
        setIsFormSubmenuOpen(false);
        setIsGeomSubmenuOpen(false);
        setIsRequestSubmenuOpen(false);
        setSelectedGeom(null);
    }, [handleMeasurementClick, isMeasuring, setIsFormSubmenuOpen, setIsGeomSubmenuOpen, setIsRequestSubmenuOpen]);

    const MapInitializer = () => {
        const map = useMap();

        useEffect(() => {
            if (map && !mapRef.current) {
                mapRef.current = map;
                setIsMapReady(true);
            }
        }, [map]);

        return null;
    };

    useEffect(() => {
        if (isFormSubmenuOpen || isRequestSubmenuOpen) {
            setIsGeomSubmenuOpen(false);
        }
        if (!isFormSubmenuOpen && !isRequestSubmenuOpen) {
            setSelectedGeom(null);
        }
    }, [isFormSubmenuOpen, isRequestSubmenuOpen]);

    return (
        <div style={{ display: "flex", width: '100%', height: '100%', minHeight: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                {isDropdownOpen && (
                    <GeoDropDown geometries={geojsonData} onGeometrySelect={handleClick}/>
                )}
                <IconButton
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: isDropdownOpen ? '250px' : '0px',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        zIndex: 1200,
                        '&:hover': {
                            backgroundColor: '#f0f0f0'
                        }
                    }}
                >
                    {isDropdownOpen ? <ArrowBackIosNewIcon sx={{fontSize: '1rem'}} /> : <ArrowForwardIosIcon sx={{fontSize: '1rem'}} />}
                </IconButton>
            </Box>

            <Box sx={{ position: "relative", flexGrow: 1, height: '100%', minHeight: 0 }}>
                <MapContainer
                        center={[34.91667, 33.625]}
                        zoom={9}
                        style={mapContainerStyle}
                    >
                        <MapInitializer />
                        <LayerManager />
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {selectedGeom && (
                        <GeoJSON
                            key={`selected-geom-${Date.now()}`}
                            data={selectedGeom.geometry}
                            eventHandlers={{ 
                                click: (e) => {
                                    L.DomEvent.stopPropagation(e);
                                    handleClick(selectedGeom);
                                } 
                            }}
                            style={{ color: 'blue', weight: 2 }}
                        >
                            <Tooltip>{selectedGeom.name ? selectedGeom.name : "New"}</Tooltip>
                        </GeoJSON>
                    )}
                    <MapClickHandler onMapClick={handleMapClick} />
                </MapContainer>
                <Box
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        zIndex: 1200,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1
                    }}
                >
                    <button
                        type="button"
                        onClick={handleToggleMeasurement}
                        style={{
                            padding: "6px 12px",
                            borderRadius: 4,
                            border: "1px solid #1976d2",
                            backgroundColor: isMeasuring ? "#1976d2" : "#fff",
                            color: isMeasuring ? "#fff" : "#1976d2",
                            cursor: "pointer",
                            fontWeight: 600,
                            boxShadow: "0 1px 4px rgba(0,0,0,0.2)"
                        }}
                    >
                        {isMeasuring ? "Finish Measuring" : "Measure Distance"}
                    </button>
                    {isMeasuring && (
                        <small
                            style={{
                                background: "rgba(255,255,255,0.9)",
                                padding: "4px 8px",
                                borderRadius: 4,
                                border: "1px solid #e0e0e0",
                                fontSize: "0.75rem",
                                color: "#333"
                            }}
                        >
                            Click two points on the map to measure.
                        </small>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    width: isFormSubmenuOpen || isRequestSubmenuOpen || isGeomSubmenuOpen ? "25%" : "0%",
                    height: '100%',
                    maxHeight: '100%',
                    minHeight: 0,
                    transition: "width 0.3s ease-in-out",
                    flexShrink: 0,
                    backgroundColor: "#fff",
                    borderLeft: isFormSubmenuOpen || isRequestSubmenuOpen || isGeomSubmenuOpen ? "1px solid #e0e0e0" : "none",
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 1, py: 2, pb: 4 }}>
                    {isGeomSubmenuOpen && selectedGeom && (
                        <GeometrySubMenu
                            selectedGeom={selectedGeom}
                            editableGeom={editableGeom} // This will now just display the geometry
                            onClose={handleCloseMenu}
                            onGeomUpdate={handleGeomUpdate}
                        />
                    )}
                    {!isGeomSubmenuOpen && isFormSubmenuOpen && (
                        <FormComponent
                            selectedGeom={selectedGeom}
                            prefillRequest={replicatedRequest}
                            onPrefillConsumed={handlePrefillConsumed}
                        />
                    )}
                    {!isGeomSubmenuOpen && isRequestSubmenuOpen && (
                        <MasterRequest 
                            geometries={geojsonData}
                            onViewOnMap={handleViewRequestGeometry}
                            onReplicateRequest={handleReplicateRequest}
                        />
                    )}
                </Box>
            </Box>
        </div>
    );
};

export default MapComponent;
